// Assistance côté élève (maquette eleve.html) : la messagerie de l'élève devient « Assistance ».
// À côté des conversations (sous l'en-tête sur téléphone) : délai estimé par le serveur, position
// dans la file, disponibilité par matière et formulaire « Nouvelle demande ».
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { TON_TICKET, heure, rang, texteEstimation } from "../../services/assistance";
import MessagesView from "../MessagesView";
import "./Assistance.css";

function CarteDelai({ demande }) {
  const e = demande.estimation;
  return (
    <section className="qas-card">
      <h2><i className="bi bi-stopwatch me-2"></i>Temps de prise en charge estimé</h2>
      <div className="qas-delai">
        <div className="qas-delai-valeur">{demande.statut === "en_cours" ? "Prise en charge" : texteEstimation(e)}</div>
        <div className="qas-muted">{demande.statut === "en_cours"
          ? "Ton enseignant s'occupe de ta question."
          : e?.disponible ? "Estimation indicative, non garantie" : e?.raison}</div>
      </div>
      {demande.statut === "en_attente" && (
        <div className="qas-ligne"><i className="bi bi-people"></i><strong>{rang(e?.position || 1)} dans la file d'attente</strong></div>
      )}
      <div className="qas-ligne"><i className="bi bi-person-video3"></i>Enseignant : <strong>{demande.enseignant.nom}</strong></div>
      {demande.matiere && <div className="qas-ligne"><i className="bi bi-journal-bookmark"></i>Matière : <strong>{demande.matiere.name}</strong></div>}
      <div className="qas-ligne"><i className="bi bi-clipboard-check"></i>Statut :
        <span className={`ql-ticket-chip ql-ticket-chip--${TON_TICKET[demande.statut]}`}>{demande.statut_label}</span></div>
      <div className="qas-muted qas-maj">Mise à jour à {heure(demande.mise_a_jour)}</div>
    </section>
  );
}

function FormDemande({ enseignants, matieres, onClose, onEnvoyee }) {
  const [matiere, setMatiere] = useState(matieres[0] ? String(matieres[0].id) : "");
  const [avant, setAvant] = useState(null);
  const [question, setQuestion] = useState("");
  const [fichier, setFichier] = useState(null);
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  // Qui recevrait la demande (répartition égale entre les enseignants disponibles) et délai estimé.
  useEffect(() => {
    if (!matiere) return;
    setAvant(null);
    api.get(`assistance/estimation/?matiere=${matiere}`).then((r) => setAvant(r.data)).catch(() => setAvant(false));
  }, [matiere]);
  const profs = useMemo(() => enseignants.filter((e) => e.matieres.some((m) => String(m.id) === matiere)), [enseignants, matiere]);
  const envoyer = async (ev) => {
    ev.preventDefault();
    if (!matiere) return setErreur("Choisis la matière de ta question.");
    if (!question.trim() && !fichier) return setErreur("Décris ton problème ou joins une photo de l'exercice.");
    setEnvoi(true); setErreur("");
    const fd = new FormData();
    fd.append("matiere", matiere);
    fd.append("question", question.trim());
    if (fichier) fd.append("file", fichier);
    try {
      const r = await api.post("assistance/demandes/", fd, { transformRequest: [(data, headers) => { delete headers["Content-Type"]; return data; }] });
      onEnvoyee(r.data);
    } catch (err) {
      setErreur(err.response?.data?.detail || "Envoi impossible. Réessaie.");
    } finally { setEnvoi(false); }
  };
  return (
    <div className="ql-msg-overlay" onClick={onClose}>
      <form className="ql-msg-modal qas-modal" onClick={(e) => e.stopPropagation()} onSubmit={envoyer}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="fw-bold">Nouvelle demande d'assistance</span>
          <button type="button" className="btn-close btn-sm" onClick={onClose} aria-label="Fermer"></button>
        </div>
        {matieres.length === 0 ? <p className="qas-muted">Aucun enseignant n'a encore renseigné ses matières.</p> : <>
          <label className="qas-label">Matière
            <select className="qas-input" value={matiere} onChange={(e) => setMatiere(e.target.value)}>
              {matieres.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          {profs.length > 0 && (
            <div className="qas-profs-matiere">
              {profs.map((e) => (
                <span key={e.id} className={`qas-statut qas-statut--${e.statut}`}>{e.nom} · {e.en_ligne && e.statut === "disponible" ? "en ligne" : e.statut_label.toLowerCase()}</span>
              ))}
            </div>
          )}
          <label className="qas-label">Ta question
            <textarea className="qas-input" rows={5} value={question} onChange={(e) => setQuestion(e.target.value)}
              placeholder="Décris l'exercice et le point que tu ne comprends pas…" />
          </label>
          <label className="qas-label">Photo ou PDF de l'exercice (facultatif)
            <input className="qas-input" type="file" accept="image/*,.pdf" onChange={(e) => setFichier(e.target.files?.[0] || null)} />
          </label>
          <div className="qas-estimation-avant">
            <i className="bi bi-stopwatch"></i>
            {avant === null ? <span>Calcul du délai…</span> : avant === false || !avant.destinataire ? (
              <span>Estimation indisponible.{avant?.estimation?.raison ? ` ${avant.estimation.raison}` : ""}</span>
            ) : (
              <span>Ta demande ira à <strong>{avant.destinataire.nom}</strong>{avant.destinataire.admin ? " (administrateur : aucun enseignant de cette matière n'est disponible)" : ""}.
                {" "}Délai estimé : <strong>{texteEstimation(avant.estimation)}</strong> · tu serais <strong>{rang(avant.estimation?.position || 1)}</strong> dans sa file.
                {!avant.estimation?.disponible && avant.estimation?.raison && <small className="d-block">{avant.estimation.raison}</small>}</span>
            )}
          </div>
          <p className="qas-muted">Les demandes sont réparties à parts égales entre les enseignants disponibles de la matière.
            Ensuite, tu pourras continuer l'échange avec tout le clavier : formules, dessin, fichiers et messages vocaux.</p>
        </>}
        {erreur && <div className="alert alert-danger py-2 mt-2 mb-0">{erreur}</div>}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="btn btn-light" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={envoi || !matieres.length}>{envoi ? "Envoi…" : "Envoyer ma demande"}</button>
        </div>
      </form>
    </div>
  );
}

export default function AssistanceEleve({ user }) {
  const [donnees, setDonnees] = useState(null);
  const [form, setForm] = useState(false);
  const [ouvrir, setOuvrir] = useState(null);
  const [replie, setReplie] = useState(true);
  const charger = useCallback(() => {
    api.get("assistance/eleve/").then((r) => setDonnees(r.data)).catch(() => setDonnees((d) => d || false));
  }, []);
  useEffect(() => {
    charger();
    // L'estimation suit la file : elle est rafraîchie régulièrement.
    const t = setInterval(charger, 30000);
    return () => clearInterval(t);
  }, [charger]);
  const demandes = donnees?.demandes || [];
  const resume = demandes[0];
  return (
    <div className="qas-page">
      <div className="qas-conversations">
        <MessagesView user={user} titre="Assistance" libelleNouveau="Nouvelle demande" onNouveau={() => setForm(true)}
          convAOuvrir={ouvrir} onChange={charger} />
      </div>
      <aside className={`qas-aside${replie ? " qas-aside--replie" : ""}`} aria-label="Suivi de mes demandes">
        <button type="button" className="qas-resume" onClick={() => setReplie((r) => !r)} aria-expanded={!replie}>
          <i className="bi bi-stopwatch"></i>
          <span>{resume ? (resume.statut === "en_cours" ? `Prise en charge par ${resume.enseignant.nom}`
            : `${rang(resume.estimation?.position || 1)} dans la file · ${texteEstimation(resume.estimation)}`) : "Aucune demande en cours"}</span>
          <i className={`bi bi-chevron-${replie ? "down" : "up"}`}></i>
        </button>
        <div className="qas-aside-contenu">
          <button className="btn btn-primary qas-nouvelle" onClick={() => setForm(true)} disabled={!donnees}>
            <i className="bi bi-pencil-square me-1"></i>Nouvelle demande
          </button>
          {donnees === null ? <div className="qas-card qas-muted">Chargement…</div> : donnees === false ? (
            <div className="qas-card qas-muted">Le suivi des demandes est momentanément indisponible.</div>
          ) : <>
            {demandes.map((d) => <CarteDelai key={d.id} demande={d} />)}
            {!demandes.length && (
              <section className="qas-card">
                <h2><i className="bi bi-stopwatch me-2"></i>Temps de prise en charge estimé</h2>
                <p className="qas-muted">Aucune demande en cours. Choisis la matière de ta question : le délai estimé s'affiche avant l'envoi.</p>
              </section>
            )}
            <section className="qas-card">
              <h2>Disponibilité par matière</h2>
              {donnees.matieres.map((m) => (
                <div key={m.id} className="qas-matiere">
                  <span><span className={`qas-point${m.disponibles ? "" : " qas-point--off"}`}></span>{m.name}</span>
                  <span>{`${m.disponibles} disponible${m.disponibles > 1 ? "s" : ""}`}</span>
                </div>
              ))}
              {!donnees.matieres.length && <p className="qas-muted">Aucune matière renseignée par les enseignants.</p>}
            </section>
            <section className="qas-card">
              <h2>💡 Pour être aidé rapidement</h2>
              <p>Indique ta classe, la matière et la question précise. Tu peux joindre une photo de l'exercice.</p>
              <p className="qas-muted">Une notification te préviendra dès qu'un enseignant prendra ta demande en charge.</p>
            </section>
          </>}
        </div>
      </aside>
      {form && donnees && (
        <FormDemande enseignants={donnees.enseignants} matieres={donnees.matieres} onClose={() => setForm(false)}
          onEnvoyee={(r) => { setForm(false); setOuvrir({ id: r.conversation_id, cle: Date.now() }); charger(); }} />
      )}
    </div>
  );
}
